import { Component } from '@theme/component';

const CACHE_KEY = 'currency-notice:usd-mxn';
const CACHE_TTL = 12 * 60 * 60 * 1000;

/** Free, keyless USD → MXN sources, tried in order. */
const RATE_SOURCES = [
  {
    url: 'https://api.frankfurter.dev/v1/latest?base=USD&symbols=MXN',
    /** @param {any} data */
    read: (data) => data?.rates?.MXN,
  },
  {
    url: 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json',
    /** @param {any} data */
    read: (data) => data?.usd?.mxn,
  },
];

/** @type {Promise<number | null> | null} */
let ratePromise = null;

/**
 * Resolves today's MXN per 1 USD rate, cached per browser for a few hours and shared by every notice on the page.
 *
 * @returns {Promise<number | null>}
 */
function getUsdRate() {
  if (ratePromise) return ratePromise;

  ratePromise = (async () => {
    try {
      const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.timestamp < CACHE_TTL && cached.rate > 0) return cached.rate;
    } catch (error) {
      // Storage can be unavailable; fall through to the network.
    }

    for (const source of RATE_SOURCES) {
      try {
        const response = await fetch(source.url);
        if (!response.ok) continue;

        const rate = Number(source.read(await response.json()));
        if (!(rate > 0)) continue;

        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify({ rate, timestamp: Date.now() }));
        } catch (error) {
          // Caching is optional.
        }
        return rate;
      } catch (error) {
        // Try the next source.
      }
    }

    return null;
  })();

  return ratePromise;
}

/**
 * Shows the approximate USD amount of a price in MXN using today's exchange rate, and can place the notice right
 * before an element rendered by an app (for example the Experiences "Book Now" button). Without JavaScript, or if the
 * rates can't be fetched, the notice keeps the server-rendered amount based on the rate set in the block.
 *
 * The notice box (`[data-currency-notice]`) and the USD amount (`[data-usd-price]`, rendered by the translation) are
 * looked up once, because the box leaves this element when it is placed next to the target.
 */
class CurrencyNoticeComponent extends Component {
  /** @type {HTMLElement | null} */
  #notice = null;

  /** @type {HTMLElement | null} */
  #usdPrice = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // The notice may already live outside this element after being placed next to the target.
    this.#notice ??= this.querySelector('[data-currency-notice]');
    this.#usdPrice ??= this.#notice?.querySelector('[data-usd-price]') ?? null;

    this.#placeNotice();
    this.#updateAmount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#notice?.remove();
  }

  /**
   * Moves the notice right before the target element and keeps it there if the app re-renders.
   */
  #placeNotice() {
    const selector = this.dataset.insertBefore;
    const scope = this.parentElement;
    if (!selector || !scope || this.#observer) return;

    const place = () => {
      const target = scope.querySelector(selector);
      const notice = this.#notice;
      if (!target || !notice || notice.nextElementSibling === target) return;
      target.before(notice);
    };

    place();
    this.#observer = new MutationObserver(place);
    this.#observer.observe(scope, { childList: true, subtree: true });
  }

  async #updateAmount() {
    const priceMin = Number(this.dataset.priceMin);
    const priceMax = Number(this.dataset.priceMax);
    if (!(priceMin >= 0) || !this.#usdPrice) return;

    const rate = await getUsdRate();
    if (!rate || !this.isConnected) return;

    const format = (/** @type {number} */ cents) =>
      `US$${Math.round(cents / 100 / rate).toLocaleString('en-US')}`;

    this.#usdPrice.textContent =
      priceMax > priceMin ? `${format(priceMin)} – ${format(priceMax)}` : format(priceMin);
  }
}

if (!customElements.get('currency-notice-component')) {
  customElements.define('currency-notice-component', CurrencyNoticeComponent);
}
